import {
  useCreateClaim,
  useUpdateClaim,
  useCreateSubmission,
  useBulkCreateClaims,
} from 'api/resource';
import {
  type CreateMutationDataParams,
  type BulkCreateMutationDataParams,
  updateClaimMutationData,
  type UpdateMutationDataParams,
} from 'api/utils/MutationDataBuilder';
import { PayorEnum } from 'interfaces';
import type { Claim, CreateSubmission, SubmissionType } from 'interfaces';
import { claimSchema } from '@billing/validation/claim';
import {
  CLAIM_TARIFF_STATUS,
  UNRECONCILED_CLAIM_TARIFF_STATUSES,
} from '@billing/validation/tariff';
import {
  claimHasAnyTariffStatus,
  claimHasUnreconciledTariffStatus,
  isUnreconciledTariffStatus,
  normalizeLegacyTariffStatus,
} from 'utils/claimStatusUtils';

import type { MutableRefObject } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useClaimStatusChangeNotification } from '../useClaimStatusChangeNotification';
import { useClaimBatchCreatedNotification } from '../useClaimBatchCreatedNotification';

interface ClaimHandlersProps {
  dirtyFields: MutableRefObject<{ [key: string]: boolean }>;
  resetDirtyFields: () => void;
}

export const useClaimHandlers = ({
  dirtyFields,
  resetDirtyFields,
}: ClaimHandlersProps) => {
  const { id: siteID } = useCurrentSiteStore();
  const createClaimQuery = useCreateClaim();
  const bulkCreateClaimQuery = useBulkCreateClaims();
  const updateClaimQuery = useUpdateClaim();
  const notifyClaimStatusChange = useClaimStatusChangeNotification();
  const notifyBatchCreated = useClaimBatchCreatedNotification();

  const createSubmission = useCreateSubmission();

  const queryPending = createSubmission.isPending;

  const handleCreateClaim = async ({
    values,
    table,
  }: CreateMutationDataParams<Claim>) => {
    delete values.referringProviderObject;
    const createdClaim = await createClaimQuery.mutateAsync({ ...values });
    table?.setCreatingRow(null);
    notifyClaimStatusChange(undefined, createdClaim);
  };

  const formatCreditsAndDebits = (values: Claim) => {
    if (
      values.payor?.name !== PayorEnum.wcb &&
      values.payor?.name !== PayorEnum.blueCross
    )
      return { credits: null, debits: null };
    const credits = [];
    const debits = [];
    // The credits and debits are 1:1 for array sizes so we can do one loop to syncronize data and filter
    for (let i = 0; i < values?.debits?.length; i++) {
      if (values.debits[i].paidAmount) {
        const amount = values.debits[i].paidAmount;
        debits.push(values.debits[i]);
        credits.push({ ...values.credit[i], amount: amount }); // overwrite the amount (which is null) with the paidAmount
      }
    }
    return { credits, debits };
  };

  const handleUpdateClaim = async ({
    values,
    row,
    table,
  }: UpdateMutationDataParams<Claim>) => {
    const isValidClaim = claimSchema.safeParse(values).success;
    const hasStagedTariffActions = Boolean(
      dirtyFields.current.__stagedTariffActions
    );
    const isTariffReconciliationFlow =
      claimHasUnreconciledTariffStatus(values) || hasStagedTariffActions;
    const isTariffResubmissionFlow = claimHasAnyTariffStatus(values, [
      CLAIM_TARIFF_STATUS.REJECTED,
      ...UNRECONCILED_CLAIM_TARIFF_STATUSES,
    ]);

    if (
      isValidClaim &&
      isTariffReconciliationFlow &&
      dirtyFields.current.debits &&
      values.debits?.every(
        (debit) => (debit.requestedAmount || 0) === (debit.paidAmount || 0)
      )
    ) {
      let didUpdateTariffStatus = false;
      // Only reconcile tariff rows that are currently unreconciled.
      // Preserve submitted/rejected/other statuses on mixed-status claims.
      values.tariffs = (values.tariffs || []).map((tariff) => {
        const normalizedStatus = normalizeLegacyTariffStatus(tariff.status);
        const isTariffUnreconciled =
          normalizedStatus && isUnreconciledTariffStatus(normalizedStatus);

        if (!isTariffUnreconciled) {
          return tariff;
        }

        didUpdateTariffStatus = true;
        return {
          ...tariff,
          status: CLAIM_TARIFF_STATUS.PAID,
        };
      });
      if (didUpdateTariffStatus) {
        dirtyFields.current.tariffs = true;
      }
    } else if (isValidClaim && isTariffResubmissionFlow && values.resubmit) {
      values.tariffs = (values.tariffs || []).map((tariff) => ({
        ...tariff,
        status: CLAIM_TARIFF_STATUS.READY,
      }));
      dirtyFields.current.tariffs = true;
    }

    const newData = JSON.parse(JSON.stringify(values));
    // Keep reconciliation flows from reshaping debit/credit payloads mid-transition.
    if (!isTariffReconciliationFlow) {
      const { credits, debits } = formatCreditsAndDebits(values);
      dirtyFields.current.debits = !!debits;
      dirtyFields.current.credits = !!credits;
      newData.credits = credits;
      newData.debits = debits;
    }

    delete values.resubmit;
    if (values.patient) {
      delete values.patient.healthcards;
    }
    const outgoingDirtyFields = { ...dirtyFields.current };
    delete outgoingDirtyFields.__stagedTariffActions;

    const updateData = {
      dirtyFields: outgoingDirtyFields,
      newData,
    };
    const oldData = row;
    const savedClaim = await updateClaimMutationData<Claim>({
      table,
      values: updateData,
      row: oldData,
      mutation: updateClaimQuery,
    });
    resetDirtyFields();
    table.resetExpanded();
    notifyClaimStatusChange(row.original, savedClaim);
  };

  const handleBulkClaimCreation = async ({
    values,
    table,
    createdBy,
  }: BulkCreateMutationDataParams<Claim>) => {
    const result = await bulkCreateClaimQuery.mutateAsync({
      values,
      siteID,
      createdBy,
    });
    table?.setCreatingRow(null);
    const batchID = result?.claims[0]?.batchID;
    if (batchID) notifyBatchCreated(batchID);
  };

  const handleCreateSubmission = async (
    values: CreateSubmission,
    type: SubmissionType,
    email: string
  ) => {
    return createSubmission.mutateAsync({ data: values, type, email });
  };

  return {
    handleCreateClaim,
    handleBulkClaimCreation,
    handleUpdateClaim,
    handleCreateSubmission,
    queryPending,
    createClaimQuery,
  };
};
